import TitleBar from '../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { Footer } from '../../../components/common/Footer';
import {
  FETCH_DIVISIONS_INVENTORY_BY_ID,
  UPDATE_DIVISIONS_INVENTORY,
  UPDATE_PRODUCT_GROUPS_INVENTORY,
  UPDATE_PRODUCTS_INVENTORY,
} from '../queries';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import FinancialData from '../../../components/common/FinancialData';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

type Inventory = {
  monthsOnHand: number;
  revenueEquivalent: number;
};

type FindInventoryByDivision = {
  __typename: string;
  inventories: Inventory[];
  isDefault: boolean;
  name: string;
  key: string;
  children?: FindInventoryByDivision[];
};

interface DataReturned {
  findInventoryByDivision: FindInventoryByDivision;
}

type InventoryTransformedData = {
  revenueEquivalent: number;
  monthsOnHand: number;
  children: InventoryTransformedData[];
  isDefault: boolean;
  name: string;
  key: string;
  __typename: string;
};

function DivisionDetail() {
  const params = useParams();
  const divisionId = params?.divisionId!;

  const { isAdmin, canEdit } = useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_DIVISIONS_INVENTORY_BY_ID,
    {
      variables: {
        uuid: divisionId,
      },
    },
  );

  const [updateDivisionsInventory, { loading: divisionInventoryLoading }] =
    useMutation(UPDATE_DIVISIONS_INVENTORY, {
      refetchQueries: ['GetDivisionsInventoryById', 'GetDivisionsInventory'],
    });

  const [
    updateProductGroupsInventory,
    { loading: productGroupInventoryLoading },
  ] = useMutation(UPDATE_PRODUCT_GROUPS_INVENTORY, {
    refetchQueries: ['GetDivisionsInventoryById', 'GetDivisionsInventory'],
  });

  const [updateProductsInventory, { loading: productInventoryLoading }] =
    useMutation(UPDATE_PRODUCTS_INVENTORY, {
      refetchQueries: ['GetDivisionsInventoryById', 'GetDivisionsInventory'],
    });

  const divisionInventory: FindInventoryByDivision[] = [
    data?.findInventoryByDivision!,
  ];

  const loadingStates =
    loading ||
    divisionInventoryLoading ||
    productGroupInventoryLoading ||
    productInventoryLoading;

  const mutationMapping = {
    DivisionFinancial: updateDivisionsInventory,
    ProductGroupFinancial: updateProductGroupsInventory,
    ProductFinancial: updateProductsInventory,
  };

  const convertReturnedData = (
    data: FindInventoryByDivision[],
  ): InventoryTransformedData[] => {
    return data?.map((inventory) => {
      return {
        revenueEquivalent: inventory?.inventories[0]?.revenueEquivalent,
        monthsOnHand: inventory?.inventories[0]?.monthsOnHand,
        isDefault: inventory?.isDefault,
        name: inventory?.name,
        key: inventory?.key,
        __typename: inventory?.__typename,
        children: convertReturnedData(inventory?.children!),
      };
    });
  };

  const columns: ColumnsType<InventoryTransformedData> = [
    {
      title: 'Products',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Finished Goods Inventory',
      dataIndex: 'monthsOnHand',
      key: 'monthsOnHand',
      width: 200,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.monthsOnHand, b.monthsOnHand),
      render: (text: string) => (
        <div className="custom-table--column flex justify-end items-center">
          <span className="font-normal   text-primary-200">
            {text ?? 0}
            {' months'}
          </span>
        </div>
      ),
    },
    {
      title: 'Revenue Equivalent',
      dataIndex: 'revenueEquivalent',
      key: 'revenueEquivalent',
      width: 200,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.revenueEquivalent, b.revenueEquivalent),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
  ];

  const formFields = [
    {
      label: 'Months on Hand',
      key: 'monthsOnHand',
      type: 'time',
      decoratorWord: 'months',
      isRequired: true,
    },
  ];

  const modifyVariables = async ({
    values,
    currentRecord,
  }: ModifyVariablesArgs) => {
    const monthsOnHand = parseFloat(values.monthsOnHand);

    const productTypeMapping: Record<string, any> = {
      DivisionFinancial: { division: { uuid: currentRecord?.key } },
      ProductGroupFinancial: { productGroup: { uuid: currentRecord?.key } },
      ProductFinancial: { product: { uuid: currentRecord?.key } },
    };

    return {
      mutation:
        mutationMapping[
          currentRecord.__typename as keyof typeof mutationMapping
        ],
      ...productTypeMapping[currentRecord.__typename],
      monthsOnHand,
    };
  };

  return (
    <div>
      <TitleUpdater title={`Division Detail`} />
      <TitleBar title="Finished Goods Inventory" />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Division Detail"
          columns={columns}
          data={convertReturnedData(divisionInventory)}
          loading={loadingStates}
          formFields={formFields}
          inlineEditing
          editable
          editableOptions={{
            create: false,
            update: isAdmin || canEdit,
            delete: false,
          }}
          modifyVariables={modifyVariables}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/finished-goods-inventory'} />
    </div>
  );
}

export default DivisionDetail;
